import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PersonalizationFlow } from '@/components/PersonalizationFlow';
import { CornerIcon } from '@/components/CornerIcon';
import { OptimizedVideoAnimation } from '@/components/performance/OptimizedVideoAnimation';

import { useNavigate } from 'react-router-dom';
import { getSupabaseIconUrl, getFeatureIconUrl, getAnimationUrl, SUPABASE_ICONS } from '@/utils/supabaseIcons';

const MockLanding = () => {
  const [showPersonalization, setShowPersonalization] = useState(false);
  const [selectedOption, setSelectedOption] = useState<'option1' | 'option2' | 'option3'>('option1');
  const navigate = useNavigate();

  if (showPersonalization) {
    return <PersonalizationFlow onComplete={() => setShowPersonalization(false)} />;
  }

  const renderOption1 = () => (
    <div className="min-h-screen" style={{background: 'var(--nm-bg)'}}>
      <CornerIcon />
      
      {/* Hero Section - Neumorphic styled with enhanced spacing */}
      <section className="container mx-auto spacing-mobile spacing-mobile-y pt-24 sm:pt-32 lg:pt-40 pb-8 sm:pb-16">
        <div className="text-center max-w-4xl mx-auto">
          {/* Hero Animation Container - Neumorphic styled */}
          <div className="mb-12 flex justify-center">
            <div className="nm-hero-card p-8">
              <OptimizedVideoAnimation
                src={getAnimationUrl('lyra-landing-page.mp4')}
                fallbackIcon={
                  <img 
                    src={getSupabaseIconUrl(SUPABASE_ICONS.heroMain)} 
                    alt="Lyra AI Mentor - Your intelligent learning companion" 
                    className="w-31 h-31 sm:w-34 sm:h-34 md:w-40 md:h-40 object-contain" 
                  />
                }
                className="w-31 h-31 sm:w-34 sm:h-34 md:w-40 md:h-40"
                context="ui"
                autoPlay={true}
                loop={true}
                muted={true}
              />
            </div>
          </div>

          <div className="nm-badge nm-badge-primary mb-4 sm:mb-6 px-4 sm:px-6 py-2 text-xs sm:text-sm font-medium inline-block">
            For Non-Profit & Public Interest Professionals
          </div>
          
          <h1 className="text-responsive-xl font-bold mb-4 sm:mb-6 text-purple-600 leading-tight text-wrap-safe">
            Master AI Without the Mystery
          </h1>
          
          <p className="text-responsive-md text-gray-600 mb-6 sm:mb-8 leading-relaxed max-w-2xl mx-auto text-wrap-safe">
            Transform your impact with AI-powered workflows. Learn practical tools, navigate ethics, and boost your mission - all with your personal AI mentor, Lyra.
          </p>

          {/* Option 1: What You Get - Quick value highlights */}
          <div className="text-center mb-8 sm:mb-12">
            <div className="nm-highlight-bar p-4 sm:p-6 rounded-lg bg-cyan-50 border border-cyan-100 shadow-md">
              <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-8 text-sm sm:text-base">
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 bg-purple-600 text-white rounded-full text-xs font-semibold">135+</span>
                  <span className="text-gray-700 font-medium">Professional Prompts</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 bg-purple-600 text-white rounded-full text-xs font-semibold">7</span>
                  <span className="text-gray-700 font-medium">Hands-on Chapters</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 bg-purple-600 text-white rounded-full text-xs font-semibold">9</span>
                  <span className="text-gray-700 font-medium">Role Categories</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-center mb-8 sm:mb-12">
            <button className="nm-button nm-button-primary px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold" onClick={() => setShowPersonalization(true)}>
              Start Your AI Journey
            </button>
          </div>
          
          {/* Key Benefits - Neumorphic feature cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-8 sm:mt-16">
            <div className="nm-feature-card p-6 text-center">
              <div className="nm-icon w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-3 sm:mb-4">
                <img src={getFeatureIconUrl('learningTarget')} alt="Tailored Learning" className="w-14 h-14 sm:w-16 sm:h-16 object-contain rounded-lg" />
              </div>
              <h3 className="font-semibold text-base sm:text-lg mb-2 text-wrap-safe">Tailored Learning</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed text-wrap-safe">Personalized to your role, tech comfort, and learning style</p>
            </div>
            
            <div className="nm-feature-card p-6 text-center">
              <div className="nm-icon w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-3 sm:mb-4">
                <img src={getFeatureIconUrl('missionHeart')} alt="Mission-Focused" className="w-14 h-14 sm:w-16 sm:h-16 object-contain rounded-lg" />
              </div>
              <h3 className="font-semibold text-base sm:text-lg mb-2 text-wrap-safe">Mission-Focused</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed text-wrap-safe">Practical examples from fundraising to program delivery</p>
            </div>
            
            <div className="nm-feature-card p-6 text-center sm:col-span-2 lg:col-span-1">
              <div className="nm-icon w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-3 sm:mb-4">
                <img src={getFeatureIconUrl('achievementTrophy')} alt="Confidence Building" className="w-14 h-14 sm:w-16 sm:h-16 object-contain rounded-lg" />
              </div>
              <h3 className="font-semibold text-base sm:text-lg mb-2 text-wrap-safe">Confidence Building</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed text-wrap-safe">Build expertise through real AI-powered lessons with clear milestones and achievements</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );

  const renderOption2 = () => (
    <div className="min-h-screen" style={{background: 'var(--nm-bg)'}}>
      <CornerIcon />
      
      {/* Hero Section - Neumorphic styled with enhanced spacing */}
      <section className="container mx-auto spacing-mobile spacing-mobile-y pt-24 sm:pt-32 lg:pt-40 pb-8 sm:pb-16">
        <div className="text-center max-w-4xl mx-auto">
          {/* Hero Animation Container - Neumorphic styled */}
          <div className="mb-12 flex justify-center">
            <div className="nm-hero-card p-8">
              <OptimizedVideoAnimation
                src={getAnimationUrl('lyra-landing-page.mp4')}
                fallbackIcon={
                  <img 
                    src={getSupabaseIconUrl(SUPABASE_ICONS.heroMain)} 
                    alt="Lyra AI Mentor - Your intelligent learning companion" 
                    className="w-31 h-31 sm:w-34 sm:h-34 md:w-40 md:h-40 object-contain" 
                  />
                }
                className="w-31 h-31 sm:w-34 sm:h-34 md:w-40 md:h-40"
                context="ui"
                autoPlay={true}
                loop={true}
                muted={true}
              />
            </div>
          </div>

          <div className="nm-badge nm-badge-primary mb-4 sm:mb-6 px-4 sm:px-6 py-2 text-xs sm:text-sm font-medium inline-block">
            For Non-Profit & Public Interest Professionals
          </div>
          
          <h1 className="text-responsive-xl font-bold mb-4 sm:mb-6 text-purple-600 leading-tight text-wrap-safe">
            Master AI Without the Mystery
          </h1>
          
          <p className="text-responsive-md text-gray-600 mb-6 sm:mb-8 leading-relaxed max-w-2xl mx-auto text-wrap-safe">
            Transform your impact with AI-powered workflows. Learn practical tools, navigate ethics, and boost your mission - all with your personal AI mentor, Lyra.
          </p>
          
          <div className="flex justify-center mb-8 sm:mb-12">
            <button className="nm-button nm-button-primary px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold" onClick={() => setShowPersonalization(true)}>
              Start Your AI Journey
            </button>
          </div>
          
          {/* Option 2: Enhanced feature cards with prompt library highlight */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-8 sm:mt-16">
            <div className="nm-feature-card p-6 text-center">
              <div className="nm-icon w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-3 sm:mb-4">
                <img src={getFeatureIconUrl('learningTarget')} alt="Tailored Learning" className="w-14 h-14 sm:w-16 sm:h-16 object-contain rounded-lg" />
              </div>
              <h3 className="font-semibold text-base sm:text-lg mb-2 text-wrap-safe">Tailored Learning</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed text-wrap-safe">Personalized to your role, tech comfort, and learning style</p>
            </div>
            
            {/* Enhanced Prompt Library Feature Card */}
            <div className="nm-feature-card p-6 text-center border-2 border-cyan-200 bg-white shadow-lg">
              <div className="nm-icon w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-3 sm:mb-4">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-purple-100 rounded-lg flex items-center justify-center">
                  <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
              <h3 className="font-semibold text-base sm:text-lg mb-2 text-wrap-safe text-purple-800">135+ Ready-to-Use Prompts</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed text-wrap-safe">From fundraising to program management - tested prompts across 9 specialized categories</p>
              <div className="mt-3">
                <span className="inline-block px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">Professional Toolkit</span>
              </div>
            </div>
            
            <div className="nm-feature-card p-6 text-center sm:col-span-2 lg:col-span-1">
              <div className="nm-icon w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-3 sm:mb-4">
                <img src={getFeatureIconUrl('achievementTrophy')} alt="Confidence Building" className="w-14 h-14 sm:w-16 sm:h-16 object-contain rounded-lg" />
              </div>
              <h3 className="font-semibold text-base sm:text-lg mb-2 text-wrap-safe">Confidence Building</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed text-wrap-safe">Build expertise through real AI-powered lessons with clear milestones and achievements</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );

  const renderOption3 = () => (
    <div className="min-h-screen" style={{background: 'var(--nm-bg)'}}>
      <CornerIcon />
      
      {/* Hero Section - Neumorphic styled with enhanced spacing */}
      <section className="container mx-auto spacing-mobile spacing-mobile-y pt-24 sm:pt-32 lg:pt-40 pb-8 sm:pb-16">
        <div className="text-center max-w-4xl mx-auto">
          {/* Hero Animation Container - Neumorphic styled */}
          <div className="mb-12 flex justify-center">
            <div className="nm-hero-card p-8">
              <OptimizedVideoAnimation
                src={getAnimationUrl('lyra-landing-page.mp4')}
                fallbackIcon={
                  <img 
                    src={getSupabaseIconUrl(SUPABASE_ICONS.heroMain)} 
                    alt="Lyra AI Mentor - Your intelligent learning companion" 
                    className="w-31 h-31 sm:w-34 sm:h-34 md:w-40 md:h-40 object-contain" 
                  />
                }
                className="w-31 h-31 sm:w-34 sm:h-34 md:w-40 md:h-40"
                context="ui"
                autoPlay={true}
                loop={true}
                muted={true}
              />
            </div>
          </div>

          <div className="nm-badge nm-badge-primary mb-4 sm:mb-6 px-4 sm:px-6 py-2 text-xs sm:text-sm font-medium inline-block">
            For Non-Profit & Public Interest Professionals
          </div>
          
          <h1 className="text-responsive-xl font-bold mb-4 sm:mb-6 text-purple-600 leading-tight text-wrap-safe">
            Master AI Without the Mystery
          </h1>
          
          {/* Option 3: Enhanced longer description with line breaks */}
          <div className="text-responsive-md text-gray-600 mb-6 sm:mb-8 leading-relaxed max-w-3xl mx-auto text-wrap-safe">
            <p className="mb-4">
              Transform your impact with AI-powered workflows through 40+ hands-on lessons and 135+ professional prompts. Learn practical tools, navigate complex ethics, and amplify your mission with training designed specifically for nonprofit and public interest professionals.
            </p>
            <p>
              From fundraising and donor communications to program management and data storytelling - master the AI skills that matter most to your work. All with your personal AI mentor, Lyra, providing guidance, feedback, and support every step of the way.
            </p>
          </div>
          
          <div className="flex justify-center mb-8 sm:mb-12">
            <button className="nm-button nm-button-primary px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold" onClick={() => setShowPersonalization(true)}>
              Start Your AI Journey
            </button>
          </div>
          
          {/* Key Benefits - Original feature cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-8 sm:mt-16">
            <div className="nm-feature-card p-6 text-center">
              <div className="nm-icon w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-3 sm:mb-4">
                <img src={getFeatureIconUrl('learningTarget')} alt="Tailored Learning" className="w-14 h-14 sm:w-16 sm:h-16 object-contain rounded-lg" />
              </div>
              <h3 className="font-semibold text-base sm:text-lg mb-2 text-wrap-safe">Tailored Learning</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed text-wrap-safe">Personalized to your role, tech comfort, and learning style</p>
            </div>
            
            <div className="nm-feature-card p-6 text-center">
              <div className="nm-icon w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-3 sm:mb-4">
                <img src={getFeatureIconUrl('missionHeart')} alt="Mission-Focused" className="w-14 h-14 sm:w-16 sm:h-16 object-contain rounded-lg" />
              </div>
              <h3 className="font-semibold text-base sm:text-lg mb-2 text-wrap-safe">Mission-Focused</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed text-wrap-safe">Practical examples from fundraising to program delivery</p>
            </div>
            
            <div className="nm-feature-card p-6 text-center sm:col-span-2 lg:col-span-1">
              <div className="nm-icon w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-3 sm:mb-4">
                <img src={getFeatureIconUrl('achievementTrophy')} alt="Confidence Building" className="w-14 h-14 sm:w-16 sm:h-16 object-contain rounded-lg" />
              </div>
              <h3 className="font-semibold text-base sm:text-lg mb-2 text-wrap-safe">Confidence Building</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed text-wrap-safe">Build expertise through real AI-powered lessons with clear milestones and achievements</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );

  return (
    <div className="min-h-screen">
      {/* Option Selector */}
      <div className="fixed top-4 left-4 z-50 bg-white rounded-lg shadow-lg p-4">
        <h3 className="font-semibold mb-3">Landing Page Options</h3>
        <div className="space-y-2">
          <button 
            onClick={() => setSelectedOption('option1')}
            className={`block w-full px-3 py-2 text-left rounded ${
              selectedOption === 'option1' 
                ? 'bg-purple-100 text-purple-800 border-2 border-purple-300' 
                : 'bg-gray-100 hover:bg-gray-200'
            }`}
          >
            Option 1: Stats Bar
          </button>
          <button 
            onClick={() => setSelectedOption('option2')}
            className={`block w-full px-3 py-2 text-left rounded ${
              selectedOption === 'option2' 
                ? 'bg-purple-100 text-purple-800 border-2 border-purple-300' 
                : 'bg-gray-100 hover:bg-gray-200'
            }`}
          >
            Option 2: Enhanced Cards
          </button>
          <button 
            onClick={() => setSelectedOption('option3')}
            className={`block w-full px-3 py-2 text-left rounded ${
              selectedOption === 'option3' 
                ? 'bg-purple-100 text-purple-800 border-2 border-purple-300' 
                : 'bg-gray-100 hover:bg-gray-200'
            }`}
          >
            Option 3: Enhanced Text
          </button>
        </div>
        <button 
          onClick={() => navigate('/')}
          className="mt-3 text-sm text-gray-600 hover:text-gray-800"
        >
          ← Back to Original
        </button>
      </div>

      {/* Render Selected Option */}
      {selectedOption === 'option1' ? renderOption1() : 
       selectedOption === 'option2' ? renderOption2() : 
       renderOption3()}
    </div>
  );
};

export default MockLanding;